"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { Button } from "@ui/shad"
import { format } from "date-fns"
import { FileText, Loader2 } from "lucide-react"

import {
  deleteDraft,
  getDraft,
  publicationDraftKey,
  purgeExpiredDrafts,
  putDraft,
  type IntakeFormDraft,
} from "@/lib/intake-form-drafts"
import {
  fetchPublicIntakeFormPublicationBookingAttempt,
  fetchPublicIntakeFormPublicationSchedulingAvailability,
  fetchPublicIntakeFormPublicationUploadReservations,
  PublicIntakeFormError,
  useCreatePublicIntakeFormPublicationSubmission,
  useFetchPublicIntakeFormPublication,
  useFetchPublicIntakeFormPublicationHtml,
  useUploadPublicIntakeFormPublicationDocument,
  type PublicIntakeFormSubmissionPayload,
} from "@/hooks/api/public-intake-forms"
import { FormRunner } from "@/components/public-intake-forms/form-runner"
import { PublicNotice } from "@/components/public-intake-forms/public-notice"
import { PublicShell } from "@/components/public-intake-forms/public-shell"
import type { FormBridgeInit, FormBridgeTerminalState } from "@/components/public-intake-forms/use-form-bridge"

const UPDATED_NOTICE = "This form was updated. We carried over the answers that still apply."

export function PublicIntakeFormPublication({ token }: { token: string }) {
  const draftKey = publicationDraftKey(token)

  const publication = useFetchPublicIntakeFormPublication(token)
  const uploadDocument = useUploadPublicIntakeFormPublicationDocument()
  const createSubmission = useCreatePublicIntakeFormPublicationSubmission()

  const [draftLoaded, setDraftLoaded] = useState(false)
  const [draft, setDraft] = useState<IntakeFormDraft | null>(null)
  const [pinnedVersionId, setPinnedVersionId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [terminalState, setTerminalState] = useState<FormBridgeTerminalState>(null)
  const [attempt, setAttempt] = useState(0)

  // Visitors land directly in the form — a saved draft resumes automatically,
  // pinned to the version it was written against.
  useEffect(() => {
    let cancelled = false
    void purgeExpiredDrafts()
      .then(() => getDraft(draftKey))
      .then((stored) => {
        if (cancelled) return
        setDraft(stored)
        if (stored) setPinnedVersionId(stored.versionId)
        setDraftLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [draftKey])

  const currentVersionId = publication.data?.version_id ?? null
  const activeVersionId = pinnedVersionId ?? currentVersionId
  const formName = publication.data?.form_name ?? "Form"

  // Every session pins to the first version it loads, so a publication refetch
  // after the advisor publishes a new version cannot remount the runner and
  // discard in-flight answers. The pin is cleared only by the explicit
  // fall-forward paths below, which re-pin to whatever is current.
  useEffect(() => {
    if (!draftLoaded || pinnedVersionId || !currentVersionId) return
    setPinnedVersionId(currentVersionId)
  }, [draftLoaded, pinnedVersionId, currentVersionId])

  const html = useFetchPublicIntakeFormPublicationHtml(token, draftLoaded && !terminalState ? activeVersionId : null)

  // A pinned version only survives while it stays submittable. Once the backend
  // retires it, fall forward onto the published one and carry answers across.
  useEffect(() => {
    if (!(html.error instanceof PublicIntakeFormError) || html.error.status !== 410) return
    if (!pinnedVersionId) return
    setPinnedVersionId(null)
    setNotice(UPDATED_NOTICE)
  }, [html.error, pinnedVersionId])

  const init = useMemo<FormBridgeInit | null>(() => {
    if (!draftLoaded) return null
    if (!draft) {
      return {
        answers: {},
        attestations: {},
        touchedKeys: [],
        nav: { activeStepKey: null, completed: {} },
        source: "none",
        sensitivePaths: [],
      }
    }
    return {
      answers: draft.answers,
      attestations: draft.attestations,
      touchedKeys: draft.touchedKeys,
      nav: draft.nav,
      source: "draft",
      sensitivePaths: draft.sensitivePaths ?? [],
    }
  }, [draft, draftLoaded])

  const uploadFile = useCallback(
    ({ fieldKey, clientSubmissionId, file }: { fieldKey: string; clientSubmissionId: string; file: File }) =>
      uploadDocument.mutateAsync({ token, versionId: activeVersionId as string, clientSubmissionId, fieldKey, file }),
    [activeVersionId, token, uploadDocument]
  )

  const fetchUploadReservations = useCallback(
    (clientSubmissionId: string) => fetchPublicIntakeFormPublicationUploadReservations(token, clientSubmissionId),
    [token]
  )

  const submitForm = useCallback(
    (submission: PublicIntakeFormSubmissionPayload) =>
      createSubmission.mutateAsync({ token, versionId: activeVersionId as string, submission }),
    [activeVersionId, createSubmission, token]
  )

  const fetchSchedulingAvailability = useCallback(
    (fieldKey: string) =>
      fetchPublicIntakeFormPublicationSchedulingAvailability({
        token,
        versionId: activeVersionId as string,
        fieldKey,
      }),
    [activeVersionId, token]
  )

  const pollBookingAttempt = useCallback(
    ({ attemptId, clientSubmissionId }: { attemptId: string; clientSubmissionId: string }) =>
      fetchPublicIntakeFormPublicationBookingAttempt({ token, attemptId, clientSubmissionId }),
    [token]
  )

  const startFresh = async () => {
    await deleteDraft(draftKey)
    setDraft(null)
    setPinnedVersionId(null)
    setNotice(null)
    setAttempt((value) => value + 1)
  }

  // Re-read from storage rather than reusing the draft loaded at page mount:
  // the bridge has been saving over it for the whole session, and the remount
  // below reseeds the runtime from whatever this puts in state.
  const continueOnCurrentVersion = async () => {
    if (!currentVersionId) return
    const stored = await getDraft(draftKey)
    const rewritten = stored ? await putDraft({ ...stored, versionId: currentVersionId }) : null
    setDraft(rewritten ?? stored)
    setPinnedVersionId(null)
    setTerminalState(null)
    setNotice(UPDATED_NOTICE)
    setAttempt((value) => value + 1)
  }

  if (publication.isLoading || !draftLoaded) {
    return (
      <PublicShell>
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
      </PublicShell>
    )
  }

  if (publication.isError || !publication.data) {
    return (
      <PublicNotice
        icon={<FileText className="text-muted-foreground mx-auto size-9" />}
        title="Form unavailable"
        description={publication.error?.message || "This link is invalid or no longer active."}
      />
    )
  }

  const details = publication.data

  if (details.status === "closed" || terminalState === "revoked") {
    return (
      <PublicNotice
        icon={<FileText className="text-muted-foreground mx-auto size-9" />}
        title="Form unavailable"
        description={`This form is no longer accepting responses. Contact ${details.company_name} if you still need to complete it.`}
      />
    )
  }

  if (terminalState === "version_gone") {
    return (
      <PublicNotice
        icon={<FileText className="text-muted-foreground mx-auto size-9" />}
        title="This form was updated"
        description="Your answers were not submitted. Continue on the updated form and we will carry over what still applies."
      >
        <Button onClick={() => void continueOnCurrentVersion()}>Continue on the updated form</Button>
      </PublicNotice>
    )
  }

  if (init && activeVersionId) {
    const resumeBanner = draft ? (
      <span>
        Welcome back — we restored the answers you saved {format(new Date(draft.updatedAt), "MMM d 'at' h:mm aaa")}.{" "}
        <button type="button" onClick={() => void startFresh()} className="font-medium underline underline-offset-2">
          Start over
        </button>
      </span>
    ) : null

    return (
      <FormRunner
        key={`${activeVersionId}:${attempt}`}
        title={formName}
        localDraftKey={draftKey}
        versionId={activeVersionId}
        init={init}
        html={html}
        uploadFile={uploadFile}
        fetchUploadReservations={fetchUploadReservations}
        submitForm={submitForm}
        fetchSchedulingAvailability={fetchSchedulingAvailability}
        pollBookingAttempt={pollBookingAttempt}
        notice={notice ?? resumeBanner}
        onTerminal={setTerminalState}
      />
    )
  }

  return (
    <PublicShell>
      <Loader2 className="text-muted-foreground size-6 animate-spin" />
    </PublicShell>
  )
}
